/* eslint-disable @typescript-eslint/no-use-before-define */
import axios from 'axios'

import { toggleModal } from 'components/core/ErrorBoundary/ErrorServerToast/ErrorServerToast-component'
import { clearAccessTokens, getAccessToken, getRefreshToken, setAccessTokens } from 'utils/accessHelpers'
import { APP_BASE_URL, APP_URL, IS_PRODUCTION } from 'utils/helperConfig'

const url = `${APP_BASE_URL}/api/v1`
const urlV2 = `${APP_BASE_URL}/api/v2`
const urlV3 = `${APP_BASE_URL}/api/v3`
const urlV4 = `${APP_BASE_URL}/api/v4`
const urlV5 = `${APP_BASE_URL}/api/v5`
const timeout = 100000
const timeoutBlob = 60000

if (navigator.cookieEnabled) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${getAccessToken()}`
}

let API_URL = APP_URL

const serializeApiError = error => error?.response?.data?.errors?.[0]?.message || error
const serializeApiErrorCode = error => error?.response?.data?.errors?.[0]?.errorCode || error

if (!IS_PRODUCTION) {
    API_URL = `${window.location.protocol}//${window.location.host}`
}

async function getJson(endpoint) {
    const resp = await axios.get(`${API_URL}${endpoint}`, { timeout })

    return resp
}

async function getRequest(endpoint) {
    const resp = await axios.get(`${url}${endpoint}`, { timeout })

    return resp
}
async function getRequestWithBlob(endpoint) {
    try {
        const resp = await axios.get(`${url}${endpoint}`, { timeoutBlob, responseType: 'blob' })
        const filename = resp?.headers?.['content-disposition']?.split('filename=')?.[1]?.split(';')?.[0]
        let newResp = resp

        if (filename) {
            const newData = Object.assign(resp.data, { filename })

            newResp = Object.assign(resp, { newData })
        }

        return newResp
    } catch (error) {
        if (
            error.request.responseType === 'blob' &&
            error.response.data instanceof Blob &&
            error.response.data.type &&
            error.response.data.type.toLowerCase().indexOf('json') !== -1
        ) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader()

                reader.onload = () => {
                    error.response.data = JSON.parse(reader.result)
                    resolve(Promise.reject(error))
                }

                reader.onerror = () => {
                    reject(error)
                }

                reader.readAsText(error.response.data)

                return error
            })
        }
    }
}

async function getRequestWithBlobV2(endpoint) {
    try {
        const resp = await axios.get(`${urlV2}${endpoint}`, { timeoutBlob, responseType: 'blob' })
        const filename = resp?.headers?.['content-disposition']?.split('filename=')?.[1]?.split(';')?.[0]
        let newResp = resp

        if (filename) {
            const newData = Object.assign(resp.data, { filename })

            newResp = Object.assign(resp, { newData })
        }

        return newResp
    } catch (error) {
        if (
            error.request.responseType === 'blob' &&
            error.response.data instanceof Blob &&
            error.response.data.type &&
            error.response.data.type.toLowerCase().indexOf('json') !== -1
        ) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader()

                reader.onload = () => {
                    error.response.data = JSON.parse(reader.result)
                    resolve(Promise.reject(error))
                }

                reader.onerror = () => {
                    reject(error)
                }

                reader.readAsText(error.response.data)

                return error
            })
        }
    }
}

async function getRequestV2(endpoint) {
    const resp = await axios.get(`${urlV2}${endpoint}`, { timeout })

    return resp
}

async function getRequestV3(endpoint) {
    const resp = await axios.get(`${urlV3}${endpoint}`, { timeout })

    return resp
}

async function getRequestV4(endpoint) {
    const resp = await axios.get(`${urlV4}${endpoint}`, { timeout })

    return resp
}

async function getRequestV5(endpoint) {
    const resp = await axios.get(`${urlV5}${endpoint}`, { timeout })

    return resp
}

async function postRequest(endpoint, body = {}) {
    const resp = await axios.post(`${url}${endpoint}`, body, { timeout })

    return resp
}

async function postRequestV2(endpoint, body = {}) {
    const resp = await axios.post(`${urlV2}${endpoint}`, body, { timeout })

    return resp
}

async function postRequestV3(endpoint, body = {}) {
    const resp = await axios.post(`${urlV3}${endpoint}`, body, { timeout })

    return resp
}

async function postRequestV4(endpoint, body = {}) {
    const resp = await axios.post(`${urlV4}${endpoint}`, body, { timeout })

    return resp
}

async function putRequest(endpoint, body = {}) {
    const resp = await axios.put(`${url}${endpoint}`, body, { timeout })

    return resp
}

async function putRequestV2(endpoint, body = {}) {
    const resp = await axios.put(`${urlV2}${endpoint}`, body, { timeout })

    return resp
}

async function putRequestV3(endpoint, body = {}) {
    const resp = await axios.put(`${urlV3}${endpoint}`, body, { timeout })

    return resp
}

async function patchRequest(endpoint, body = {}) {
    const resp = await axios.patch(`${url}${endpoint}`, body, { timeout })

    return resp
}

async function patchRequestV2(endpoint, body = {}) {
    const resp = await axios.patch(`${urlV2}${endpoint}`, body, { timeout })

    return resp
}

async function deleteRequest(endpoint, body = {}) {
    const resp = await axios.delete(`${url}${endpoint}`, body, { timeout })

    return resp
}

async function deleteRequestV2(endpoint, body = {}) {
    const resp = await axios.delete(`${urlV2}${endpoint}`, body, { timeout })

    return resp
}

axios.interceptors.response.use(
    response => response,
    async error => {
        if (error.code === 'ECONNABORTED') {
            toggleModal(true, 'connectionError')

            return Promise.reject({ ...error, response: { ...error?.response, data: { ...error?.response?.data, errors: undefined } } })
        }

        if (
            (window.location.href.includes('/sign-in') || window.location.href.includes('/sign-up')) &&
            error?.response?.status >= 500 &&
            error?.response?.status < 600
        ) {
            toggleModal(true, 'serverMaintenance')

            return Promise.reject({ ...error, response: { ...error?.response, data: { ...error?.response?.data, errors: undefined } } })
        }

        if (error?.response?.status >= 500 && error?.response?.status < 600) {
            toggleModal(true, 'serverError')

            return Promise.reject({ ...error, response: { ...error?.response, data: { ...error?.response?.data, errors: undefined } } })
        }

        if (error?.message === 'Network Error' && !error?.response) {
            toggleModal(true, 'connectionError')
        }

        if (error?.response?.status !== 401) {
            return Promise.reject(error)
        }

        if ((window.location.href.includes('/sign-in') || window.location.href.includes('/sign-up')) && error?.response?.status === 401) {
            try {
                return Promise.reject(error)
            } catch (error) {
                return Promise.reject(error)
            }
        }

        if (
            ((error?.response?.status === 401 || error?.response?.status === 500) && error?.config?.url.includes('refresh')) ||
            !getRefreshToken()
        ) {
            clearAccessTokens()
            window.location.href = '/sign-in'

            return Promise.reject(error)
        }

        if (error?.response?.status === 401 && error?.response?.config && !error?.response?.config.__isRetryRequest) {
            return getAuthToken().then(response => {
                const {
                    data: { accessToken, refreshToken, refreshTokenExpiresAt },
                } = response

                setAccessTokens(accessToken, refreshToken, refreshTokenExpiresAt)
                const errorConfig = error.config

                errorConfig.headers['Authorization'] = `Bearer ${accessToken}`
                error.response.config.__isRetryRequest = true

                return axios(error.response.config)
            })
        }
    }
)

let authTokenRequest

function getAuthToken() {
    if (!authTokenRequest) {
        authTokenRequest = postRequest('/auth/refresh', { refreshToken: getRefreshToken() })
        authTokenRequest.then(resetAuthTokenRequest, resetAuthTokenRequest)
    }

    return authTokenRequest
}

function resetAuthTokenRequest() {
    authTokenRequest = null
}

export {
    deleteRequest,
    deleteRequestV2,
    getJson,
    getRequest,
    getRequestV2,
    getRequestV3,
    getRequestV4,
    getRequestV5,
    getRequestWithBlob,
    getRequestWithBlobV2,
    patchRequest,
    patchRequestV2,
    postRequest,
    postRequestV2,
    postRequestV3,
    postRequestV4,
    putRequest,
    putRequestV2,
    putRequestV3,
    serializeApiError,
    serializeApiErrorCode,
}
